import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  IMergeField,
  MailrailBaseEmailCampaign,
  TEmailCampaignMailing,
  Template,
} from '@feathr/blackbox';
import { TemplateClass } from '@feathr/blackbox';
import { CardV2 as Card, Fieldset, Input, Well } from '@feathr/components';
import type { ICampaignValidationErrors } from '@feathr/extender/App/EventsPage/CampaignSummary';
import EmailTemplateSelect from '@feathr/extender/components/EmailTemplateSelect';
import MergetagSelect from '@feathr/extender/components/MergetagSelect';
import { useStore } from '@feathr/extender/state';
import { flattenError } from '@feathr/hooks';

export interface IEmailTemplateConfigProps {
  /** Parent Mailrail campaign: provides the template clone context (event/parent) and partner/pinpoint flags. */
  campaign: MailrailBaseEmailCampaign;
  /** Write target for the template field: the campaign for step 0, an ephemeral step for 1..N. */
  mailing: TEmailCampaignMailing;
  /** The resolved template for `mailing`, supplied by the composer; undefined when none is selected. */
  template?: Template;
  validationErrors: ICampaignValidationErrors;
  disabled?: boolean;
  /** 'card' (default) renders a titled card; 'well' renders a flat white well for the drip-builder panel. */
  variant?: 'card' | 'well';
}

/**
 * The Email Template card for a single Mailrail mailing: the template selector plus the subject,
 * merge-tag, and preview-text fields. The selected template is supplied by the composer (so it can
 * be fetched in bulk) and edited in place.
 *
 * @param props - {@link IEmailTemplateConfigProps}.
 */
function EmailTemplateConfig({
  campaign,
  mailing,
  template,
  validationErrors,
  disabled = false,
  variant = 'card',
}: Readonly<IEmailTemplateConfigProps>): JSX.Element {
  const { t } = useTranslation();
  const { Templates } = useStore();

  const [hadFocusSubject, setHadFocusSubject] = useState(false);
  const [mergetagTemplate] = useState(() =>
    Templates.create({ _cls: TemplateClass.SESEmail, account: campaign.get('account') }),
  );

  const handleSelectOption = useCallback(
    (option: IMergeField) => {
      if (!template) {
        return;
      }
      const nextSubject = (template.get('subject') || '').concat(option.value);
      template.set({ subject: nextSubject });
      if (!template.get('used_mergefields').includes(option.value)) {
        template.addMergefield(option.value);
      }
    },
    [template, template?.get('used_mergefields'), template?.get('subject')],
  );

  function handleChangeSubject(newValue?: string): void {
    if (template) {
      template.set({ subject: newValue });
    }
  }

  function handleBlurSubject(): void {
    setHadFocusSubject(true);
  }

  function handleChangePreviewText(newValue?: string): void {
    if (template) {
      template.set({ preview_text: newValue });
    }
  }

  const fields = (
    <>
      <EmailTemplateSelect
        campaign={campaign}
        disabled={disabled}
        mailing={mailing}
        template={template}
      />
      <Fieldset>
        <Input
          disabled={disabled || !template || template.isReadOnly}
          helpPlacement={'bottom'}
          helpText={
            template
              ? t('The subject line for the email your audience will receive.')
              : t('Select a template to edit the subject.')
          }
          isLoading={template?.isUpdating || template?.isPending}
          label={t('Email subject')}
          name={'email_subject'}
          onBlur={handleBlurSubject}
          onChange={handleChangeSubject}
          required={true}
          type={'text'}
          validationError={hadFocusSubject ? flattenError(validationErrors.subject) : undefined}
          value={template?.get('subject')}
        />
        <MergetagSelect
          disabled={disabled || !template || template.isReadOnly}
          helpPlacement={'bottom'}
          helpText={t(
            'You can use merge tags to personalize the subject line of your message. Choose the data you want to merge in from this dropdown to insert it at the end of your current subject line.',
          )}
          isPartnerMessage={campaign.isAnyPartnerMessage}
          isPinpoint={campaign.isPinpoint}
          name={'merge_tags'}
          onChange={handleSelectOption}
          template={mergetagTemplate}
        />
        <Input
          disabled={disabled || !template || template.isReadOnly}
          helpPlacement={'bottom'}
          helpText={
            template
              ? t(
                  'The preview text that displays along with the subject line in some email clients.',
                )
              : t('Select a template to edit the preview text.')
          }
          label={t('Preview text')}
          onChange={handleChangePreviewText}
          optional={true}
          type={'text'}
          value={template?.get('preview_text')}
        />
      </Fieldset>
    </>
  );

  return variant === 'card' ? (
    <Card>
      <Card.Header title={t('Email Template')} />
      <Card.Content addVerticalGap={true}>{fields}</Card.Content>
    </Card>
  ) : (
    <Well layout={'vertical'} theme={'white'}>
      {fields}
    </Well>
  );
}

export default observer(EmailTemplateConfig);
